
<script setup>
import { Icon } from '@components'
import { ref, computed, onMounted, watch } from 'vue'

import { useRoute, useRouter } from 'vue-router'

// Routing
const route = useRoute()
const router = useRouter()

const emit = defineEmits(['update:currentPage'])

const props = defineProps({
    currentPage: {
        type: Number,
        default: 1
    },
    totalItems: {
        type: Number,
        default: 10
    },
    itemsPerPage: {
        type: Number,
        default: 10
    },
})

// Current page.
const routePage = computed(() => {
    return Number(route.query.page) || props.currentPage || 1
})

const currentPage = ref(routePage.value)


// Navigation.
const pages = computed(() => {
    return Math.ceil(props.totalItems / props.itemsPerPage)
})

const next = () => {
    if (currentPage.value < pages.value) {
        currentPage.value++
    }

    router.push({
        query: {
            ...route.query,
            page: currentPage.value
        }
    })
    emit('update:currentPage', currentPage.value)
}

const prev = () => {
    if (currentPage.value > 1) {
        currentPage.value--
    }

    router.push({
        query: {
            ...route.query,
            page: currentPage.value
        }
    })
    emit('update:currentPage', currentPage.value)
}

const LIMIT = 3

const availablePages = computed(() => {

    let _pages = []

    for (let i = 1; i <= pages.value; i++) {
        // First or last 3 pages.
        if (i <= LIMIT || i > pages.value - LIMIT) {
            _pages.push(i)
        }

        // Current page, when it is not first or last page.
        if (i === currentPage.value && i !== 1 && i !== pages.value) {
            _pages.push(i)
        }

        // Pages before current page.
        if (i < currentPage.value && i > currentPage.value - LIMIT) {
            _pages.push(i)
        }

        // Pages after current page.
        if (i > currentPage.value && i < currentPage.value + LIMIT) {
            _pages.push(i)
        }

    }

    // Make it unique.
    _pages = [...new Set(_pages)]

    // Sort it.
    _pages.sort((a, b) => a - b)

    // Add dots.
    _pages = _pages.reduce((acc, curr, index, arr) => {
        if (index === 0) {
            acc.push(curr)
        } else {
            if (curr - arr[index - 1] > 1) {
                acc.push('...')
                acc.push(curr)
            } else {
                acc.push(curr)
            }
        }
        return acc
    }, [])

    return _pages
})

const setPage = (page) => {
    currentPage.value = page

    router.push({
        query: {
            ...route.query,
            page: currentPage.value
        }
    })

    emit('update:currentPage', currentPage.value)
}

// On load.
onMounted(() => {
    setPage(routePage.value)
    emit('update:currentPage', currentPage.value)
})

</script>

<template>
    <div class="flex items-center justify-between w-full text-sm select-none">
        <!-- Previous page  -->
        <div class="flex items-center gap-2 cursor-pointer px-[21.5px] !w-[136px] font-semibold" :class="{
            'opacity-40 cursor-not-allowed text-[#D0D5DD]': currentPage === 1
        }" @click.prevent="prev">
            <Icon size="20px" name="arrowLeft" />Previous
        </div>

        <!-- pages  -->
        <ul class="flex gap-0.5 items-center">
            <li v-for="page in availablePages" @click="setPage(page)" :class="{ 'bg-[#F9FAFB] text-[#1D2939]': page === currentPage }" class="hover:bg-[#F9FAFB] hover:text-[#1D2939] font-medium text-[#475467] text-center text-sm w-10 h-10 flex items-center justify-center cursor-pointer rounded-[20px]">
                <div v-if="Number(page)"> {{ page }} </div>
                <div v-else>...</div>
            </li>

        </ul>

        <!-- Next page  -->
        <div class="flex items-center gap-2 cursor-pointer px-[21.5px] !w-[136px] font-semibold" :class="{
            'opacity-40 cursor-not-allowed text-[#D0D5DD]': currentPage === pages
        }" @click.prevent="next"> Next
            <Icon size="20px" name="arrowRight" />
        </div>
    </div>
</template>
